import React from 'react';
import { Link } from 'react-router-dom';
import { Car, Youtube, Instagram, Facebook, Twitter, Linkedin, Mail, Phone } from 'lucide-react';
import { useSite } from '../contexts/SiteContext';

export default function Footer() {
  const { settings, pages } = useSite();

  const socials = [
    { url: settings.social_youtube, icon: Youtube, label: 'YouTube' },
    { url: settings.social_instagram, icon: Instagram, label: 'Instagram' },
    { url: settings.social_twitter, icon: Twitter, label: 'X / Twitter' },
    { url: settings.social_facebook, icon: Facebook, label: 'Facebook' },
    { url: settings.social_linkedin, icon: Linkedin, label: 'LinkedIn' },
  ].filter(s => s.url);

  // Prefer specific pages for footer in a specific order
  const footerSlugs = ['hakkimizda', 'kullanim-kosullari', 'gizlilik-politikasi', 'cerezler', 'iletisim'];
  const footerPages = footerSlugs
    .map(s => pages.find(p => p.slug === s))
    .filter(Boolean);

  return (
    <footer className="bg-white border-t border-slate-100 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-11 h-11 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Car className="w-6 h-6 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-extrabold text-xl text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Mini<span className="text-emerald-600">Oto</span>
              </span>
              <span className="text-[10px] text-slate-400 font-medium tracking-wide uppercase">
                Mikromobilite Topluluğu
              </span>
            </div>
          </Link>

          <nav className="flex flex-wrap items-center gap-5 text-sm text-slate-600" data-testid="footer-nav">
            {footerPages.map(p => (
              <Link key={p.slug} to={`/sayfa/${p.slug}`} className="hover:text-emerald-600" data-testid={`footer-link-${p.slug}`}>
                {p.title}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2" data-testid="footer-socials">
            {socials.map(s => (
              <a
                key={s.label}
                href={s.url}
                target="_blank"
                rel="noreferrer noopener"
                className="w-9 h-9 rounded-full bg-slate-50 hover:bg-emerald-50 flex items-center justify-center text-slate-500 hover:text-emerald-600 transition"
                title={s.label}
                data-testid={`footer-social-${s.label.toLowerCase().replace(/\W/g, '')}`}
              >
                <s.icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        {(settings.contact_email || settings.contact_phone) && (
          <div className="mt-5 flex flex-wrap gap-5 text-xs text-slate-500">
            {settings.contact_email && (
              <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-1.5 hover:text-emerald-600" data-testid="footer-contact-email">
                <Mail className="w-3.5 h-3.5" /> {settings.contact_email}
              </a>
            )}
            {settings.contact_phone && (
              <a href={`tel:${settings.contact_phone.replace(/\s+/g, '')}`} className="flex items-center gap-1.5 hover:text-emerald-600" data-testid="footer-contact-phone">
                <Phone className="w-3.5 h-3.5" /> {settings.contact_phone}
              </a>
            )}
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-slate-100 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-400">
          <span>© {new Date().getFullYear()} MiniOto. Tüm hakları saklıdır.</span>
          <span className="italic">Küçük araçlar, büyük özgürlükler.</span>
        </div>
      </div>
    </footer>
  );
}
